import type { RawVisualGrouping } from "../visualGroups";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";

export type NumericRaw = RawVisualGrouping & {
  type: "numeric";
  content: string;
  previous_response?: string;
  image?: string;
  alt_text?: string | null;
  error_message?: string;
  input_prefix?: string | null;
  input_suffix?: string | null;
  dont_know_option?: { value: string; text: string } | null;
};

export type NumericDefinition = {
  prompt: string;
  previousResponse: string;
  image?: string;
  altText?: string;
  error?: string;
  prefix?: string;
  suffix?: string;
  dontKnow?: { value: string; label: string };
};

export type NumericDraft = { value: string; dontKnowSelected: boolean };
export type NumericEvent =
  | { type: "set-value"; value: string }
  | { type: "toggle-dont-know" };
export type NumericResponse = { type: "numeric"; value: string };

export const numericCore: QuestionCore<
  NumericRaw,
  NumericDefinition,
  NumericDraft,
  NumericEvent,
  NumericResponse
> = {
  decode: (raw) => {
    if (
      raw.previous_response !== undefined &&
      typeof raw.previous_response !== "string"
    ) {
      throw new Error("Numeric previous response must be a string.");
    }
    if (raw.input_prefix != null && typeof raw.input_prefix !== "string") {
      throw new Error("Numeric input prefix must be a string.");
    }
    if (raw.input_suffix != null && typeof raw.input_suffix !== "string") {
      throw new Error("Numeric input suffix must be a string.");
    }
    return {
      prompt: raw.content,
      previousResponse: raw.previous_response ?? "",
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
      ...(raw.input_prefix ? { prefix: raw.input_prefix } : {}),
      ...(raw.input_suffix ? { suffix: raw.input_suffix } : {}),
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
    };
  },
  initialize: (definition) => {
    const dontKnowSelected =
      definition.dontKnow?.value === definition.previousResponse;
    return {
      value: dontKnowSelected ? "" : definition.previousResponse,
      dontKnowSelected,
    };
  },
  update: (_definition, draft, event) =>
    event.type === "toggle-dont-know"
      ? { value: "", dontKnowSelected: !draft.dontKnowSelected }
      : { value: event.value, dontKnowSelected: false },
  validate: (definition, draft, copy = defaultThemeV2.copy) =>
    (draft.dontKnowSelected && Boolean(definition.dontKnow)) ||
    draft.value.trim().length > 0
      ? { valid: true }
      : {
          valid: false,
          error: copy.validation.number,
          fieldId: "numeric-answer",
        },
  encode: (definition, draft) => {
    const validation = numericCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return {
      type: "numeric",
      value:
        draft.dontKnowSelected && definition.dontKnow
          ? definition.dontKnow.value
          : draft.value,
    };
  },
  projectTranscript: (definition, draft) => ({
    kind: "numeric",
    question: definition.prompt,
    answer: draft.dontKnowSelected ? definition.dontKnow!.label : draft.value,
  }),
};
